import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { isAdminSessionValid, logoutAdmin } from "../lib/api";
import {
  HiOutlineAcademicCap,
  HiOutlineArrowRightOnRectangle,
  HiOutlineBuildingOffice2,
  HiOutlineCamera,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineCube,
  HiOutlineHome,
  HiOutlineInboxStack,
  HiOutlinePhoto,
  HiOutlineRectangleGroup,
  HiOutlineRectangleStack,
  HiOutlineTag,
  HiOutlineTrophy,
  HiOutlineUserGroup,
} from "react-icons/hi2";
import "./admin.css";

/**
 * Single top-level link (no sub-menu).
 * @typedef {{ type: 'link', to: string, label: string, icon: import('react').ComponentType<{ className?: string }> }} NavLinkItem
 */

/**
 * Group with multiple sub-routes.
 * @typedef {{ type: 'group', id: string, label: string, icon: import('react').ComponentType<{ className?: string }>, children: { to: string, label: string, icon: import('react').ComponentType<{ className?: string }> }[] }} NavGroupItem
 */

/** @type {(NavLinkItem | NavGroupItem)[]} */
const NAV_STRUCTURE = [
  {
    type: "group",
    id: "home",
    label: "Home",
    icon: HiOutlineHome,
    children: [
      { to: "home/hero-banners", label: "Banners Master", icon: HiOutlineRectangleStack },
    ],
  },
  { type: "link", to: "achievements/master", label: "Achievement Master", icon: HiOutlineTrophy },
  { type: "link", to: "faculty/list", label: "Faculty list", icon: HiOutlineUserGroup },
  {
    type: "group",
    id: "gallery",
    label: "Gallery",
    icon: HiOutlinePhoto,
    children: [
      { to: "gallery/photos", label: "Photo Gallery", icon: HiOutlineCamera },
      { to: "gallery/categories", label: "Image Category Master", icon: HiOutlineTag },
    ],
  },
  {
    type: "group",
    id: "infrastructure",
    label: "Infrastructure",
    icon: HiOutlineBuildingOffice2,
    children: [
      { to: "infrastructure/items", label: "Infrastructure images", icon: HiOutlineCube },
      { to: "infrastructure/categories", label: "Infrastructure Category Master", icon: HiOutlineRectangleGroup },
    ],
  },
  { type: "link", to: "alumni/custom", label: "Custom alumni", icon: HiOutlineAcademicCap },
  { type: "link", to: "enquiries/contact", label: "Enquiries", icon: HiOutlineInboxStack },
];

function pathMatches(pathname, relTo) {
  const full = `/admin/dashboard/${relTo}`;
  return pathname === full || pathname.startsWith(`${full}/`);
}

/** @returns {string | null} */
function groupIdForPathname(pathname) {
  for (const item of NAV_STRUCTURE) {
    if (item.type === "group" && item.children.some((c) => pathMatches(pathname, c.to))) {
      return item.id;
    }
  }
  return null;
}

function initialOpenGroupIds() {
  if (typeof window === "undefined") return new Set(["home"]);
  const id = groupIdForPathname(window.location.pathname);
  if (id) return new Set([id]);
  return new Set(["home"]);
}

export default function AdminLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [openGroups, setOpenGroups] = useState(initialOpenGroupIds);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isAdminSessionValid()) {
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const id = groupIdForPathname(pathname);
    setOpenGroups(id ? new Set([id]) : new Set());
  }, [pathname]);

  const closeLogoutConfirm = useCallback(() => {
    setLogoutConfirmOpen(false);
  }, []);

  useEffect(() => {
    if (!logoutConfirmOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeLogoutConfirm();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [logoutConfirmOpen, closeLogoutConfirm]);

  const confirmLogout = async () => {
    setLogoutConfirmOpen(false);
    try {
      await logoutAdmin();
    } catch {
      /* clear local session even if API fails */
    }
    navigate("/admin/login", { replace: true });
  };

  const toggleGroup = (id) => {
    setOpenGroups((prev) => {
      if (prev.has(id)) return new Set();
      return new Set([id]);
    });
  };

  return (
    <div className="adm-layout">
      <aside className="adm-sidenav" aria-label="Admin navigation">
        <div className="adm-sidenav-brand">
          <div className="adm-sidenav-brand-mark" aria-hidden="true">
            <HiOutlineAcademicCap className="adm-sidenav-brand-icon" />
          </div>
          <div className="adm-sidenav-brand-text">
            <span className="adm-sidenav-brand-title">Srishti School</span>
            <span className="adm-sidenav-brand-sub">Admin console</span>
          </div>
        </div>

        <p className="adm-sidenav-section-label">Menu</p>
        <nav className="adm-sidenav-nav">
          {NAV_STRUCTURE.map((item) => {
            if (item.type === "link") {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    isActive ? "adm-nav-link adm-nav-link--active" : "adm-nav-link"
                  }
                  end
                >
                  <span className="adm-nav-icon-wrap">
                    <Icon className="adm-nav-svg" aria-hidden />
                  </span>
                  <span className="adm-nav-label">{item.label}</span>
                  <span className="adm-nav-row-slot" aria-hidden />
                </NavLink>
              );
            }

            const expanded = openGroups.has(item.id);
            const groupActive = item.children.some((c) => pathMatches(pathname, c.to));
            const GroupIcon = item.icon;

            return (
              <div key={item.id} className={`adm-nav-group${groupActive ? " adm-nav-group--active" : ""}`}>
                <button
                  type="button"
                  className="adm-nav-group-toggle"
                  aria-expanded={expanded}
                  onClick={() => toggleGroup(item.id)}
                >
                  <span className="adm-nav-group-toggle-main">
                    <span className={`adm-nav-icon-wrap${groupActive ? " adm-nav-icon-wrap--accent" : ""}`}>
                      <GroupIcon className="adm-nav-svg" aria-hidden />
                    </span>
                    <span className="adm-nav-label">{item.label}</span>
                  </span>
                  <span className="adm-nav-chevron" aria-hidden>
                    {expanded ? (
                      <HiOutlineChevronDown className="adm-nav-chevron-svg" />
                    ) : (
                      <HiOutlineChevronRight className="adm-nav-chevron-svg" />
                    )}
                  </span>
                </button>
                {expanded ? (
                  <div className="adm-nav-group-items">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      return (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          className={({ isActive }) =>
                            isActive
                              ? "adm-nav-link adm-nav-link--sub adm-nav-link--active"
                              : "adm-nav-link adm-nav-link--sub"
                          }
                        >
                          <span className="adm-nav-icon-wrap adm-nav-icon-wrap--sub">
                            <ChildIcon className="adm-nav-svg adm-nav-svg--sub" aria-hidden />
                          </span>
                          <span className="adm-nav-label">{child.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>

        <div className="adm-sidenav-footer">
          <button
            type="button"
            className="adm-sidenav-site-link"
            aria-label="Logout"
            onClick={() => setLogoutConfirmOpen(true)}
          >
            <HiOutlineArrowRightOnRectangle className="adm-nav-svg" aria-hidden />
            <span>Logout</span>
          </button>
        </div>
      </aside>
      <main className="adm-main">
        <Outlet />
      </main>

      {logoutConfirmOpen &&
        createPortal(
          <div className="adm-confirm-root" role="presentation">
            <button
              type="button"
              className="adm-confirm-backdrop"
              aria-label="Close dialog"
              onClick={closeLogoutConfirm}
            />
            <div
              className="adm-confirm-panel"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="adm-logout-confirm-title"
              aria-describedby="adm-logout-confirm-desc"
            >
              <h2 id="adm-logout-confirm-title" className="adm-confirm-title">
                Logout
              </h2>
              <p id="adm-logout-confirm-desc" className="adm-confirm-message">
                Are you sure you want to logout?
              </p>
              <div className="adm-confirm-actions">
                <button type="button" className="adm-btn adm-btn--ghost" onClick={closeLogoutConfirm}>
                  Cancel
                </button>
                <button type="button" className="adm-btn adm-btn--primary" onClick={confirmLogout}>
                  Logout
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
